import {
  useQueries,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { cloneDeep, isEmpty, merge } from 'lodash-es';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import isEqual from 'react-fast-compare';
import { useCatchUpParams } from 'src/components/Listings/InventoryEventPage/InventoryEventPage.hooks';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { useListExpansion } from 'src/hooks/useListExpansion';
import {
  useRefetchCatalogAndEntityOnNavigateFeature,
  useUserHasFeature,
} from 'src/hooks/useUserHasFeature';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import {
  CATCHUP_EXPANDED_QUERY_GC_TIME_IN_MILLIS,
  DATA_REFRESH_RATE_IN_MILLIS_LONG,
  DATA_REFRESH_RATE_IN_MILLIS_SHORT,
  MAX_NUM_OF_ITEMS_FOR_FLATTENED_VIEWS,
} from 'src/utils/constants/constants';
import { isInFullPageEventView, isInSearchPage } from 'src/utils/deepLinkUtils';
import { getErrorInfoFromStatusCode } from 'src/utils/errorUtils';
import {
  EmptyEntityWithTicketsQuery,
  QueryWithViewMode,
} from 'src/utils/eventQueryUtils';
import { searchEvents, sortEvents } from 'src/utils/eventWithDataUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  CatalogClient,
  CatalogCounts,
  CatalogResults,
  CatalogResultsSlim,
  EntityWithTicketsQuery,
  EventEntityCounts,
  EventWithData,
  Feature,
  InventoryViewMode,
  ListingQuery,
  PurchaseOrderQuery,
  PurchaseViewMode,
  SaleQuery,
} from 'src/WebApiController';

import { boostedEventIdsAtom } from '../AdCampaignsDataContext/useBoostedEventIdsFromCampaigns ';
import { useAppContext } from '../AppContext';
import {
  useFilterQueryContextFilterQuerySelector,
  useFilterQueryContextIsQueryInitializedSelector,
} from '../FilterQueryContext/FilterQueryContext.selectors';
import { NO_GROUP_ID } from '../MultiSelectionContext';
import { useStopwatchLoggingContext } from '../StopwatchLoggingContext/StopwatchLoggingContext';
import { StopwatchLoggingEventName } from '../StopwatchLoggingContext/StopwatchLoggingContext.types';
import {
  getStopwatchGapsForCatalogPageLoad,
  getStopwatchLoggingId,
} from '../StopwatchLoggingContext/StopwatchLoggingContext.utils';
import { useUpdateCatalogItemsInEvent } from './CatalogDataContext.hooks';
import {
  CatalogDataContextProviderProps,
  CatalogDataContextV2,
  CatalogDataContextWithSelector,
  EmptyCatalogResults,
  EventIdWithRowVersion,
  ExpandedCallbackStatus,
  ExpandedEventData,
} from './CatalogDataContext.types';
import {
  createCatalogIdbStore,
  getEventsWithNonZeroCounts,
  hasEventsWithFullLoadedData,
  loadCatalogData,
  persistCatalogData,
} from './CatalogDataContext.utils';

function CatalogDataContextV2ProviderRaw<
  TQuery extends EntityWithTicketsQuery & QueryWithViewMode,
>({
  queryKey,
  entityType,
  doNotLogPageLoad,
  getCatalogCountsCustom,
  getEventsWithNonZeroCountsCustom,
  getCatalogDataExpanded,
  onCatalogDataExpanded,
  transformEventData,
  children,
  disabled,
  disableAutoRefresh,
  ignoreMaxCount,
  filterZeroCounts = true,
}: CatalogDataContextProviderProps<TQuery>) {
  const queryClient = useQueryClient();
  const idbStore = useMemo(() => createCatalogIdbStore(), []);
  const hasGetListingsPerEventInCatchUpModeFeature = useUserHasFeature(
    Feature.GetListingsPerEventInCatchUpMode
  );
  const haCatalogDataContextWithSelectorFeature = useUserHasFeature(
    Feature.FEPerf_CatalogDataContextWithSelector
  );
  const hasRefetchCatalogAndEntityOnNavigateFeature =
    useRefetchCatalogAndEntityOnNavigateFeature(entityType);

  // changing this version will force a refetch of all queries using this key (helpful for new deploy that changes data shape)
  const queryKeyWithVersion = `${queryKey}V2`;

  const { trackError, showErrorDialog } = useClientLogContext();

  const isQueryInitialized = useFilterQueryContextIsQueryInitializedSelector();
  const [rawFilterQuery] = useFilterQueryContextFilterQuerySelector<TQuery>();

  // Backup filter query with catchup mode events
  // We dont want to put the event ids in the URL search params
  const { viagVirtualIdsList, isCatchupMode, activeViagVirtualId } =
    useCatchUpParams();
  const filterQuery = useMemo(() => {
    if (!viagVirtualIdsList || isEmpty(viagVirtualIdsList)) {
      return rawFilterQuery;
    }
    const eventOrMappingIdsWithCatchUp = Array.from(
      new Set([
        ...(rawFilterQuery.eventOrMappingIds ?? []),
        ...viagVirtualIdsList,
      ])
    );
    return {
      ...rawFilterQuery,
      eventOrMappingIds: eventOrMappingIdsWithCatchUp,
    };
  }, [rawFilterQuery, viagVirtualIdsList]);

  const { activeAccountWebClientConfig, mainRoute } = useAppContext();
  const { startEvent, addStopGap } = useStopwatchLoggingContext();
  const isEventPage = isInFullPageEventView(mainRoute);
  const isSearchPage = isInSearchPage(mainRoute);
  const eventsExpansion = useListExpansion<string>();
  const { expandedListItems } = eventsExpansion;

  const [catalogResults, setCatalogResults] = useState<CatalogResults>();

  const [errorInfo, setErrorInfo] = useState<{
    errorHeader: React.ReactNode;
    errorMessage: React.ReactNode;
  }>();

  // Flags to track the status of the expanded callback
  const [expandedCallbackStatus, setExpandedCallbackStatus] = useState<
    Record<string, ExpandedCallbackStatus>
  >({});
  const onExpandedCallbackStatusUpdate = useCallback(
    (stringQueryKey: string, status: ExpandedCallbackStatus) =>
      setExpandedCallbackStatus((prev) => ({
        ...prev,
        [stringQueryKey]: status,
      })),
    []
  );

  const {
    searchText,
    sortBy,
    isSortDescending,
    performerIds,
    venueIds,
    isBoosted,
    viewMode, // this is not used for server and keeping it in this cause refresh to happen 2x
    ...serverSideFilterQuery // Don't use it because it's a new object on every render
  } = filterQuery;

  const countFilterQuery = useMemo(() => {
    const query = Object.fromEntries(
      Object.entries(serverSideFilterQuery).filter(([_, v]) => v != null)
    );
    return {
      ...query,
      timezoneOffsetMins:
        query.timezoneOffsetMins ??
        EmptyEntityWithTicketsQuery.timezoneOffsetMins,
    } as TQuery;
  }, [serverSideFilterQuery]);

  const boostedEventIds = useAtomValue(boostedEventIdsAtom);

  const eventsFullyLoaded = useMemo(() => {
    if (catalogResults?.events == null) {
      return false; // we haven't loaded the events yet
    }

    const allEvents = Object.values(catalogResults.events);

    if (allEvents.length === 0) {
      // We've loaded the events, but there are none
      return true;
    }

    // at least 1 has both events and counts
    const result = hasEventsWithFullLoadedData(entityType, allEvents);

    return result;
  }, [catalogResults?.events, entityType]);

  /* Arguments for the catalog queries */

  const eventIdsNeedingDataWithRowVersions = useMemo(() => {
    const eventCountsMissingDataIds = Object.values(
      catalogResults?.events ?? {}
    )
      .filter((ev) => ev.event == null && ev.counts.viagVirtId != null)
      .map(
        (ev) =>
          ({
            eventId: ev.counts.viagVirtId,
            rowVer: ev.counts.rowVer,
          }) as EventIdWithRowVersion
      );

    const customEventIds =
      countFilterQuery.eventOrMappingIds?.filter(
        (id) => !catalogResults?.events[id]
      ) ?? [];

    return [
      ...eventCountsMissingDataIds,
      ...customEventIds.map(
        (id) => ({ eventId: id, rowVer: 0 }) as EventIdWithRowVersion
      ),
    ];
  }, [catalogResults, countFilterQuery.eventOrMappingIds]);

  const eventIdsNeedingData = useMemo(() => {
    return eventIdsNeedingDataWithRowVersions
      .map((id) => id.eventId)
      .toSorted();
  }, [eventIdsNeedingDataWithRowVersions]);

  /* QueryKey for the catalog queries */

  const catalogCountQueryKey = useMemo(
    () => [queryKeyWithVersion + 'Count', entityType, countFilterQuery],
    [entityType, queryKeyWithVersion, countFilterQuery]
  );

  const logKey = useMemo(
    () => JSON.stringify(catalogCountQueryKey),
    [catalogCountQueryKey]
  );

  const catalogDataQueryKey = useMemo(
    () => [queryKeyWithVersion + 'Data', eventIdsNeedingData],
    [eventIdsNeedingData, queryKeyWithVersion]
  );

  /* Conditions for the catalog queries */

  const shouldQueryCounts = useMemo(
    () =>
      activeAccountWebClientConfig.activeAccountId != null &&
      isQueryInitialized &&
      !disabled,
    [activeAccountWebClientConfig.activeAccountId, disabled, isQueryInitialized]
  );

  const shouldQueryEventData = useMemo(
    () =>
      Boolean(eventIdsNeedingData.length) &&
      activeAccountWebClientConfig.activeAccountId != null &&
      isQueryInitialized &&
      !disabled,
    [
      activeAccountWebClientConfig.activeAccountId,
      disabled,
      eventIdsNeedingData.length,
      isQueryInitialized,
    ]
  );

  const mergeCatalog = useCallback(
    (newResults: CatalogResults, data: CatalogResultsSlim) => {
      if (!data) {
        return newResults;
      }

      // Copy over the data from data
      data.events.forEach((ev) => {
        const existingEv = newResults.events[ev.virtId];
        if (existingEv) {
          existingEv.event = ev;
        } else {
          newResults.events[ev.virtId] = { event: ev } as EventWithData;
        }
        ev.posIds.forEach((posId) => {
          newResults.posIdToViagIdLookup[posId] = ev.virtId;
        });

        newResults.viagIdToLatestIdLookup[ev.virtId] = ev.virtId;
        ev.oldVirtIds?.forEach?.((oldId) => {
          newResults.viagIdToLatestIdLookup[oldId] = ev.virtId;
        });
      });

      data.performers.forEach((performer) => {
        newResults.performers[performer.viagId!] = performer;
      });

      data.venues.forEach((venue) => {
        newResults.venues[venue.viagId!] = venue;
      });

      data.countries?.forEach?.((country) => {
        if (country.code) {
          newResults.countries[country.code] = country;
        }
      });

      return newResults;
    },
    []
  );

  const mergeCatalogCounts = useCallback(
    (newResults: CatalogResults, counts?: CatalogCounts | null) => {
      if (!counts) {
        return undefined;
      }

      newResults.purchaseCount = counts.purchaseCount;

      // Reset all the counts first
      Object.values(newResults.events).forEach((ev) => {
        ev.counts = {
          listCnt: 0,
          listGrpCnt: 0,
          ungrListCnt: 0,
          salesCnt: 0,
          pricedListCnt: 0,
          tktGrpsCnt: 0,
          poCnt: 0,
        } as EventEntityCounts;
      });

      counts.entityCnts.forEach((cnts) => {
        const ev = newResults.events[cnts.viagVirtId] ?? ({} as EventWithData);
        ev.counts = cnts;

        newResults.events[cnts.viagVirtId] = ev;
      });

      return newResults;
    },
    []
  );

  /* The catalog queries */

  const refetchIntervalLong = !disableAutoRefresh
    ? DATA_REFRESH_RATE_IN_MILLIS_LONG
    : false;

  // Data query - fetches event details
  const catalogDataQuery = useQuery({
    queryKey: catalogDataQueryKey,
    queryFn: async (): Promise<CatalogResultsSlim | null> => {
      setErrorInfo(undefined);
      return tryInvokeApi(
        async () => {
          const { cachedData, realEventIdsNeedingData } = await loadCatalogData(
            idbStore,
            eventIdsNeedingDataWithRowVersions
          );

          if (realEventIdsNeedingData.length) {
            const data = await new CatalogClient(
              activeAccountWebClientConfig
            ).getCatalogSlim(realEventIdsNeedingData);

            if (data) {
              // Save the new data to idb
              persistCatalogData(idbStore, data);

              cachedData.events.push(...data.events);
              cachedData.performers.push(...data.performers);
              cachedData.venues.push(...data.venues);
              cachedData.countries.push(...data.countries);
            }
          }

          return cachedData;
        },
        (error) => {
          const { headerDisplay, messageDisplay } = getErrorInfoFromStatusCode(
            error?.status,
            error?.message
          );
          setErrorInfo({
            errorHeader: headerDisplay,
            errorMessage: messageDisplay,
          });
          trackError('CatalogClient.getCatalog', error);
        }
      );
    },
    enabled: shouldQueryEventData,
    staleTime: Infinity, // Since we're always refetching on an interval, we don't want query to calculate whether the data is stale
    refetchOnWindowFocus: false,
    refetchInterval: refetchIntervalLong as number | false,
  });

  // Counts query - fetches event counts
  const catalogCountsQuery = useQuery({
    queryKey: catalogCountQueryKey,
    queryFn: async (): Promise<CatalogCounts | null> => {
      setErrorInfo(undefined);
      return tryInvokeApi(
        async () => {
          const client = new CatalogClient(activeAccountWebClientConfig);

          if (getCatalogCountsCustom != null) {
            const counts = await getCatalogCountsCustom(countFilterQuery);
            return counts;
          } else if (entityType === ActionOutboxEntityType.Listing) {
            const counts = await client.getCatalogCountForListings(
              countFilterQuery as unknown as ListingQuery
            );
            return counts;
          } else if (entityType === ActionOutboxEntityType.Sale) {
            const counts = await client.getCatalogCountForSales(
              countFilterQuery as unknown as SaleQuery
            );
            return counts;
          } else if (entityType === ActionOutboxEntityType.Purchase) {
            const counts = await client.getCatalogCountForPurchaseOrders(
              countFilterQuery as unknown as PurchaseOrderQuery
            );
            return counts;
          }

          return null;
        },
        (error) => {
          const { headerDisplay, messageDisplay } = getErrorInfoFromStatusCode(
            error?.status,
            error?.message
          );
          setErrorInfo({
            errorHeader: headerDisplay,
            errorMessage: messageDisplay,
          });
          trackError(
            queryKeyWithVersion + 'Count: ' + entityType,
            error,
            countFilterQuery
          );
        }
      );
    },
    enabled: shouldQueryCounts,
    staleTime: hasRefetchCatalogAndEntityOnNavigateFeature ? 0 : Infinity, // If Infinity, we don't want query to calculate whether the data is stale because we're only refetch on an interval
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchInterval: refetchIntervalLong as number | false,
  });

  // Merge results from both queries into catalogResults
  useEffect(() => {
    // Wait for at least one query to have data
    if (!catalogDataQuery.data && !catalogCountsQuery.data) {
      return;
    }

    // Skip if both queries are still loading their initial data
    if (
      (shouldQueryEventData && catalogDataQuery.isLoading) ||
      (shouldQueryCounts && catalogCountsQuery.isLoading)
    ) {
      return;
    }

    const newCatalogResults = cloneDeep(catalogResults ?? EmptyCatalogResults);

    // Merge event data
    if (catalogDataQuery.data) {
      mergeCatalog(newCatalogResults, catalogDataQuery.data);
    }

    // Merge counts data
    if (catalogCountsQuery.data) {
      mergeCatalogCounts(newCatalogResults, catalogCountsQuery.data);
    }

    // Update state if anything changed
    if (newCatalogResults && !isEqual(catalogResults, newCatalogResults)) {
      setCatalogResults(newCatalogResults);
    }
  }, [
    catalogDataQuery.data,
    catalogDataQuery.isLoading,
    catalogCountsQuery.data,
    catalogCountsQuery.isLoading,
    catalogResults,
    mergeCatalog,
    mergeCatalogCounts,
    shouldQueryCounts,
    shouldQueryEventData,
  ]);

  // Handle query errors
  useEffect(() => {
    const failureReason =
      (shouldQueryEventData && catalogDataQuery.failureReason) ||
      (shouldQueryCounts && catalogCountsQuery.failureReason);

    if (failureReason) {
      const { headerDisplay, messageDisplay } = getErrorInfoFromStatusCode(
        null,
        failureReason?.toString()
      );
      setErrorInfo({
        errorHeader: headerDisplay,
        errorMessage: messageDisplay,
      });
    } else if (errorInfo) {
      setErrorInfo(undefined);
    }
  }, [
    catalogDataQuery.failureReason,
    catalogCountsQuery.failureReason,
    errorInfo,
    shouldQueryCounts,
    shouldQueryEventData,
  ]);

  const logSessionKey = useRef(logKey);
  useEffect(() => {
    if (logSessionKey.current !== logKey) {
      logSessionKey.current = logKey;

      if (shouldQueryCounts && !doNotLogPageLoad) {
        startEvent(
          logKey,
          getStopwatchLoggingId(mainRoute),
          getStopwatchGapsForCatalogPageLoad(
            isEventPage,
            isSearchPage ||
              viewMode === InventoryViewMode.FlattenedView ||
              (entityType === ActionOutboxEntityType.Purchase &&
                viewMode === PurchaseViewMode.TileView)
          ),
          entityType
        );
      }
    }
  }, [
    doNotLogPageLoad,
    entityType,
    isEventPage,
    isSearchPage,
    logKey,
    mainRoute,
    shouldQueryCounts,
    startEvent,
    viewMode,
  ]);

  const eventIdsToGetItemsFor = useMemo(() => {
    // We should get the expanded items per event in catch up mode to avoid getting listings for a big number of events
    // Which causes perf downgrading of the endpoint
    if (
      hasGetListingsPerEventInCatchUpModeFeature &&
      entityType === ActionOutboxEntityType.Listing &&
      isCatchupMode
    ) {
      if (activeViagVirtualId) {
        return [[activeViagVirtualId]];
      } else {
        // This can happen when we first load the catch up events,
        // But catchup mode relies on the activeViagVirtualId, no need to load expanded listings when it is undefined.
        return [] as [string][];
      }
    }
    // If we're on an event page, we already know the event ids we want data for, so just use those
    // If the event has no counts, it will return zero items and that will be OK
    if (
      isEventPage &&
      (filterQuery.eventOrMappingIds?.length ||
        filterQuery.oldPosEventIds?.length)
    ) {
      // We want to get all of them items for all specified ids in one go for event page
      return [
        (filterQuery.eventOrMappingIds ?? []).concat(
          filterQuery.oldPosEventIds?.map((posId) => `old:${posId}`) ?? []
        ),
      ];
    }

    if (!catalogResults) {
      return [] as [string][];
    }
    let listingCount = 0;
    let salesCount = 0;

    const eventsToGetItemsFor = (
      getEventsWithNonZeroCountsCustom ?? getEventsWithNonZeroCounts
    )(
      entityType,
      searchEvents(
        catalogResults,
        searchText,
        performerIds,
        venueIds,
        null,
        isBoosted,
        boostedEventIds
      ),
      true // requireEventNotNull
    )
      .map((ev) => {
        if (ev) {
          const evCount = ev?.counts;
          listingCount += evCount?.listCnt ?? 0;
          salesCount += evCount?.salesCnt ?? 0;
          return ev;
        }
      })
      .filter((ev) => ev)
      .map((ev) => ev!);

    let isTooManyItems = false;
    if (!ignoreMaxCount && viewMode === InventoryViewMode.FlattenedView) {
      // Only one of these are non-zero
      if (listingCount) {
        isTooManyItems = listingCount > MAX_NUM_OF_ITEMS_FOR_FLATTENED_VIEWS;
      } else if (salesCount) {
        isTooManyItems = salesCount > MAX_NUM_OF_ITEMS_FOR_FLATTENED_VIEWS;
      }
    }
    const results =
      viewMode === InventoryViewMode.FlattenedView
        ? isTooManyItems &&
          // If the catalog shows a count of way too many items and we're actually not filtering to the individual items, don't query at all
          !countFilterQuery.entityIds?.length &&
          !countFilterQuery.marketplaceEntityIds?.length
          ? []
          : // For flatten view - we know we're gonna get all of them at once, so just bundle all the event ids in 1 array
            [eventsToGetItemsFor?.map((ev) => ev.event.virtId)]
        : // For tile view, we only want 1 event per query
          expandedListItems.map((id) => [id]);

    // Finally, we need to include the PosEventIds with all the ids, because in some cases
    // the ViagogoVirtualIds do not resolve properly due to the outdated cache, and missing items are returned
    // for merged events
    return results.map((idArray) => {
      const allEvents = eventsToGetItemsFor
        .filter((ev) => idArray.includes(ev.event.virtId))
        .flatMap((ev) => ev.event.posIds.map((posId) => `old:${posId}`));
      return idArray.concat(allEvents);
    });
  }, [
    hasGetListingsPerEventInCatchUpModeFeature,
    entityType,
    isCatchupMode,
    activeViagVirtualId,
    isEventPage,
    filterQuery.eventOrMappingIds,
    filterQuery.oldPosEventIds,
    catalogResults,
    getEventsWithNonZeroCountsCustom,
    searchText,
    performerIds,
    venueIds,
    isBoosted,
    boostedEventIds,
    ignoreMaxCount,
    viewMode,
    countFilterQuery.entityIds?.length,
    countFilterQuery.marketplaceEntityIds?.length,
    expandedListItems,
  ]);

  const realExpandedDataItemsQueryKey = useMemo(
    () => [queryKeyWithVersion + 'Expanded', countFilterQuery],
    [queryKeyWithVersion, countFilterQuery]
  );

  const shouldQueryExpandedItems = useMemo(
    () =>
      activeAccountWebClientConfig.activeAccountId != null &&
      countFilterQuery != null &&
      isQueryInitialized &&
      !disabled,
    [
      activeAccountWebClientConfig.activeAccountId,
      countFilterQuery,
      isQueryInitialized,
      disabled,
    ]
  );

  const refreshInterval = !disableAutoRefresh
    ? DATA_REFRESH_RATE_IN_MILLIS_SHORT
    : false;

  const onCatalogDataExpandedCallbackCheck = useCallback(
    async (
      queryResults: UseQueryResult<
        ExpandedEventData | null | undefined,
        Error
      >[]
    ) => {
      if (!onCatalogDataExpanded) {
        return;
      }
      const queryIds = eventIdsToGetItemsFor ?? [];
      for (let index = 0; index < queryIds.length; index++) {
        const ids = queryIds[index];
        const queryKeyWithVersion = [
          ...realExpandedDataItemsQueryKey,
          ...ids.toSorted(),
        ];
        const stringQueryKey = queryKeyWithVersion.toString();
        const queryResult = queryResults[index];
        if (
          expandedCallbackStatus[stringQueryKey] ===
            ExpandedCallbackStatus.Pending &&
          queryResult?.data != null
        ) {
          onExpandedCallbackStatusUpdate(
            stringQueryKey,
            ExpandedCallbackStatus.InProgress
          );
          const newData = await onCatalogDataExpanded(
            ids,
            countFilterQuery as TQuery,
            queryResult.data
          );
          queryClient.setQueryData(queryKeyWithVersion, newData);
          onExpandedCallbackStatusUpdate(
            stringQueryKey,
            ExpandedCallbackStatus.Done
          );
        }
      }
    },
    [
      eventIdsToGetItemsFor,
      expandedCallbackStatus,
      onCatalogDataExpanded,
      onExpandedCallbackStatusUpdate,
      queryClient,
      realExpandedDataItemsQueryKey,
      countFilterQuery,
    ]
  );

  const expandedItemsQueryOptions = useMemo(
    () => ({
      queries: (eventIdsToGetItemsFor ?? []).map((ids) => {
        const queryKeyWithVersion = [
          ...realExpandedDataItemsQueryKey,
          ...ids.toSorted(),
        ];
        return {
          queryKey: queryKeyWithVersion,
          queryFn: async () => {
            if (!shouldQueryExpandedItems) {
              return null;
            }

            return tryInvokeApi(
              async () => {
                const data = await getCatalogDataExpanded?.(
                  ids[0] === NO_GROUP_ID ? [] : ids,
                  countFilterQuery as TQuery
                );
                if (onCatalogDataExpanded) {
                  onExpandedCallbackStatusUpdate(
                    queryKeyWithVersion.toString(),
                    ExpandedCallbackStatus.Pending
                  );
                }

                addStopGap(
                  logKey,
                  getStopwatchLoggingId(mainRoute),
                  getStopwatchGapsForCatalogPageLoad(
                    isEventPage,
                    isSearchPage ||
                      viewMode === InventoryViewMode.FlattenedView ||
                      (entityType === ActionOutboxEntityType.Purchase &&
                        viewMode === PurchaseViewMode.TileView)
                  ),
                  StopwatchLoggingEventName.EventitiesApiRequestReceived,
                  entityType
                );
                return data;
              },
              (error: ErrorTypes) => {
                showErrorDialog('CatalogClient.getCatalogDataExpanded', error, {
                  trackErrorData: {
                    eventIds: ids,
                    filterQuery,
                  },
                });
              }
            );
          },
          enabled: shouldQueryExpandedItems,
          staleTime: hasRefetchCatalogAndEntityOnNavigateFeature ? 0 : Infinity, // If Infinity, we don't want query to calculate whether the data is stale because we're only refetch on an interval
          gcTime: isCatchupMode
            ? CATCHUP_EXPANDED_QUERY_GC_TIME_IN_MILLIS
            : undefined,
          refetchOnWindowFocus: false,
          refetchOnMount: true,
          refetchInterval: refreshInterval as number | false,
        };
      }),
      combine: (
        results: UseQueryResult<ExpandedEventData | null | undefined, Error>[]
      ) => {
        onCatalogDataExpandedCallbackCheck(results);

        return results;
      },
    }),
    [
      eventIdsToGetItemsFor,
      realExpandedDataItemsQueryKey,
      shouldQueryExpandedItems,
      hasRefetchCatalogAndEntityOnNavigateFeature,
      isCatchupMode,
      refreshInterval,
      getCatalogDataExpanded,
      countFilterQuery,
      onCatalogDataExpanded,
      addStopGap,
      logKey,
      mainRoute,
      isEventPage,
      isSearchPage,
      viewMode,
      entityType,
      onExpandedCallbackStatusUpdate,
      showErrorDialog,
      filterQuery,
      onCatalogDataExpandedCallbackCheck,
    ]
  );

  const queryWithExpandedItems = useQueries(expandedItemsQueryOptions);

  const [eventsTransformed, setEventsTransformed] = useState<EventWithData[]>();

  // This to apply client-side filtering before transformation of the data
  useEffect(() => {
    if (!eventsFullyLoaded) {
      console.debug('eventsTransformed skipped due no complete event data');
      return;
    }

    const newExpandedData = merge(
      {},
      ...queryWithExpandedItems.map((r) => r.data ?? {})
    ) as ExpandedEventData;

    // If we're on event page, log that we have the entities loaded as well as a gap
    if (isEventPage || isSearchPage) {
      let hasEntities = false;
      switch (entityType) {
        case ActionOutboxEntityType.Listing:
          hasEntities = !!Object.values(newExpandedData).some(
            (ev) => ev.listings != null
          );
          break;
        case ActionOutboxEntityType.Sale:
          hasEntities = !!Object.values(newExpandedData).some(
            (ev) => ev.sales != null
          );
          break;

        case ActionOutboxEntityType.Purchase:
          hasEntities = !!Object.values(newExpandedData).some(
            (ev) => ev.ticketGroups != null
          );
          break;
      }
      if (hasEntities && !doNotLogPageLoad) {
        if (isEventPage) {
          addStopGap(
            logKey,
            getStopwatchLoggingId(mainRoute),
            getStopwatchGapsForCatalogPageLoad(
              isEventPage,
              isSearchPage ||
                viewMode === InventoryViewMode.FlattenedView ||
                (entityType === ActionOutboxEntityType.Purchase &&
                  viewMode === PurchaseViewMode.TileView)
            ),
            StopwatchLoggingEventName.EventEntitiesLoad,
            entityType
          );
        }

        if (isSearchPage) {
          addStopGap(
            logKey,
            getStopwatchLoggingId(mainRoute),
            getStopwatchGapsForCatalogPageLoad(
              !isSearchPage,
              isSearchPage ||
                viewMode === InventoryViewMode.FlattenedView ||
                (entityType === ActionOutboxEntityType.Purchase &&
                  viewMode === PurchaseViewMode.TileView)
            ),
            StopwatchLoggingEventName.EventEntitiesLoad,
            entityType
          );
        }
      }
    }

    const newExpandedEvents = transformEventData(
      entityType,
      searchEvents(
        catalogResults!,
        searchText,
        performerIds,
        venueIds,
        null,
        isBoosted,
        boostedEventIds
      ),
      newExpandedData,
      queryWithExpandedItems.some((q) => q.isLoading),
      filterZeroCounts
    );

    const transformed = sortEvents(
      catalogResults!,
      newExpandedEvents,
      sortBy,
      isSortDescending
    );

    if (transformed && !isEqual(transformed, eventsTransformed)) {
      if (transformed != null && !doNotLogPageLoad) {
        addStopGap(
          logKey,
          getStopwatchLoggingId(mainRoute),
          getStopwatchGapsForCatalogPageLoad(
            isEventPage,
            isSearchPage ||
              viewMode === InventoryViewMode.FlattenedView ||
              (entityType === ActionOutboxEntityType.Purchase &&
                viewMode === PurchaseViewMode.TileView)
          ),
          StopwatchLoggingEventName.EventInfoLoad,
          entityType
        );
      }
      setEventsTransformed(transformed);
      console.debug(
        'eventsTransformed updated with transformed: ' + transformed.length
      );
    }
  }, [
    catalogResults,
    entityType,
    eventsFullyLoaded,
    eventsTransformed,
    isSortDescending,
    performerIds,
    queryWithExpandedItems,
    searchText,
    sortBy,
    addStopGap,
    transformEventData,
    venueIds,
    mainRoute,
    isEventPage,
    logKey,
    doNotLogPageLoad,
    isSearchPage,
    viewMode,
    isBoosted,
    boostedEventIds,
    filterZeroCounts,
  ]);

  const refreshCatalog = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [queryKeyWithVersion + 'Count'],
      refetchType: 'active',
    });
    return await queryClient.invalidateQueries({
      queryKey: [queryKeyWithVersion + 'Data'],
      refetchType: 'active',
    });
  }, [queryClient, queryKeyWithVersion]);

  const refreshExpandedListItems = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [queryKeyWithVersion + 'Expanded'],
      refetchType: 'none', // we don't want to refetch any, we'll manually refetch the active
    });

    return queryClient.invalidateQueries({
      queryKey: realExpandedDataItemsQueryKey,
    });
  }, [queryClient, queryKeyWithVersion, realExpandedDataItemsQueryKey]);

  /**
   * Refreshes the event data for a specific item
   */
  const refreshEventForItems = useCallback(
    (itemIds: number[], entityType: ActionOutboxEntityType) => {
      const eventIds = eventsTransformed
        ?.filter((ev) => {
          if (entityType === ActionOutboxEntityType.Listing) {
            return ev.entities.listings
              ?.flatMap(flattenListingGroup)
              ?.some((l) => itemIds.includes(l.id));
          } else if (entityType === ActionOutboxEntityType.Sale) {
            return ev.entities.sales?.some((s) => itemIds.includes(s.id));
          } else if (entityType === ActionOutboxEntityType.Purchase) {
            return ev.entities.ticketGroups?.some((t) =>
              itemIds.includes(t.id)
            );
          }
          return false;
        })
        ?.map((ev) => ev.counts?.viagVirtId ?? ev.event?.virtId);

      if (eventIds?.length) {
        if (viewMode === InventoryViewMode.FlattenedView) {
          queryClient.invalidateQueries({
            queryKey: realExpandedDataItemsQueryKey,
          });
        } else {
          for (const eventId of eventIds) {
            queryClient.invalidateQueries({
              queryKey: [...realExpandedDataItemsQueryKey, eventId],
            });
          }
        }
      }
    },
    [eventsTransformed, queryClient, realExpandedDataItemsQueryKey, viewMode]
  );

  const { updateExpandedListItems, updateItemInEvent } =
    useUpdateCatalogItemsInEvent(
      queryClient,
      queryKeyWithVersion,
      realExpandedDataItemsQueryKey,
      transformEventData,
      entityType,
      searchText,
      performerIds,
      venueIds,
      isBoosted,
      boostedEventIds,
      sortBy,
      isSortDescending,
      catalogResults,
      setEventsTransformed,
      queryWithExpandedItems,
      filterZeroCounts
    );

  const allEventIds = useMemo(
    () =>
      eventsTransformed
        ?.map((ev) => ({
          viagVirtId: ev.counts?.viagVirtId ?? ev.event?.virtId,
          performerId: ev.counts?.perfId ?? ev.event?.perfId,
          venueId: ev.counts?.venId ?? ev.event?.venId,
        }))
        // Filter out the zero GUID — C# serializes Guid.Empty as "00000000-0000-0000-0000-000000000000"
        // for unmapped events. Passing it as a POSEventId filter returns zero results because no
        // real documents carry an empty GUID as their event ID.
        .filter(
          (ev) =>
            ev.viagVirtId != null &&
            ev.viagVirtId !== '00000000-0000-0000-0000-000000000000'
        ) ?? [],
    [eventsTransformed]
  );

  const isLoading = useMemo(() => {
    if (eventsTransformed == null) {
      // queries are fetching and there is no previous data
      return true;
    }
    if (!eventsFullyLoaded) {
      // event data is not fully loaded
      return true;
    }

    return catalogDataQuery.isLoading || catalogCountsQuery.isLoading;
  }, [
    catalogCountsQuery.isLoading,
    catalogDataQuery.isLoading,
    eventsFullyLoaded,
    eventsTransformed,
  ]);

  // whether the query to get items are loading
  const isItemsLoading = useMemo(
    () => queryWithExpandedItems.some((r) => r.isLoading),
    [queryWithExpandedItems]
  );

  const contextValue = useMemo(
    () => ({
      isLoading,
      isItemsLoading,
      data: catalogResults ?? undefined,
      errorInfo,
      eventsTransformed,
      allEventIds,
      logKey,
      refreshCatalog,
      updateItemInEvent,
      eventsExpansion: {
        ...eventsExpansion,
        refreshExpandedListItems,
      },
      updateExpandedListItems,
      refreshEventForItems,
      totalPurchaseCount: catalogResults?.purchaseCount,
    }),
    [
      allEventIds,
      catalogResults,
      errorInfo,
      eventsExpansion,
      eventsTransformed,
      isItemsLoading,
      isLoading,
      logKey,
      refreshCatalog,
      refreshEventForItems,
      refreshExpandedListItems,
      updateExpandedListItems,
      updateItemInEvent,
    ]
  );

  if (haCatalogDataContextWithSelectorFeature) {
    return (
      <CatalogDataContextWithSelector.Provider value={contextValue}>
        {children}
      </CatalogDataContextWithSelector.Provider>
    );
  }

  return (
    <CatalogDataContextV2.Provider value={contextValue}>
      {children}
    </CatalogDataContextV2.Provider>
  );
}

export const CatalogDataContextV2Provider = memo(
  CatalogDataContextV2ProviderRaw
) as <TQuery>(
  props: CatalogDataContextProviderProps<TQuery>
) => React.JSX.Element;
